import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSocket } from "../hooks/useSocket";

const SocketContext = createContext();

export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = !!user; // Check if user exists
  const [isInitialized, setIsInitialized] = useState(false);
  const [connectionRetryCount, setConnectionRetryCount] = useState(0);
  const maxConnectionRetries = 5;
  const connectionRetryTimeoutRef = useRef(null);
  
  // derive user id safely from possible shapes
  const currentUserId = user?.userId ?? user?._id ?? user?.id ?? null;

  // Initialize socket with user ID when authenticated
  const socket = useSocket(isAuthenticated && currentUserId ? currentUserId : null);

  // Initialize socket context
  useEffect(() => {
    if (isAuthenticated && currentUserId && !isInitialized) {
      setIsInitialized(true);
      setConnectionRetryCount(0);
    } else if (!isAuthenticated && isInitialized) {
      setIsInitialized(false);
      setConnectionRetryCount(0);
    }
  }, [isAuthenticated, currentUserId, isInitialized]);

  // Handle connection failures and retries
  useEffect(() => {
    if (socket.connectionStatus === 'failed' && connectionRetryCount < maxConnectionRetries) {
      // clear any existing timeout
      if (connectionRetryTimeoutRef.current) {
        clearTimeout(connectionRetryTimeoutRef.current);
      }
      
      // schedule retry with exponential backoff
      const retryDelay = Math.min(1000 * Math.pow(2, connectionRetryCount), 30000); // max 30 seconds
      connectionRetryTimeoutRef.current = setTimeout(() => {
        if (isAuthenticated && currentUserId) {
          socket.forceReconnect();
          setConnectionRetryCount(prev => prev + 1);
        }
      }, retryDelay);
    } else if (socket.connectionStatus === 'connected') {
      // reset retry count on successful connection
      setConnectionRetryCount(0);
    }
  }, [socket, socket.connectionStatus, connectionRetryCount, isAuthenticated, currentUserId, socket.forceReconnect]);

  // Handle page visibility changes for reconnection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        isAuthenticated &&
        currentUserId
      ) {
        // check if socket is disconnected and we're not already reconnecting
        if (!socket.isConnected && socket.connectionStatus !== 'reconnecting') {
          // clear any existing retry timeout
          if (connectionRetryTimeoutRef.current) {
            clearTimeout(connectionRetryTimeoutRef.current);
            connectionRetryTimeoutRef.current = null;
          }
          
          // attempt reconnection
          socket.forceReconnect();
        }
      }
    };

    const handleOnline = () => {
      if (isAuthenticated && currentUserId) {
        if (!socket.isConnected) {
          socket.forceReconnect();
        }
      }
    };

    const handleOffline = () => {
      // Network went offline
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [socket, isAuthenticated, currentUserId, socket.isConnected, socket.connectionStatus, socket.forceReconnect]);

  // Handle beforeunload to disconnect socket
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socket.isConnected) {
        socket.socketService.disconnect();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [socket.isConnected, socket.socketService]);

  // Handle page refresh detection and reconnection
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Page was loaded from cache (back/forward navigation)
      } else {
        // Fresh page load or refresh
        
        // if we have user but no connection, attempt to connect
        if (isAuthenticated && currentUserId && !socket.isConnected) {
          setTimeout(() => {
            socket.forceReconnect();
          }, 1000); // delay to ensure auth state is stable
        }
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [socket, isAuthenticated, currentUserId, socket.isConnected, socket.forceReconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connectionRetryTimeoutRef.current) {
        clearTimeout(connectionRetryTimeoutRef.current);
        connectionRetryTimeoutRef.current = null;
      }
    };
  }, []);

  const value = useMemo(() => ({
    ...socket,
    isInitialized,
    isAuthenticated,
    userId: currentUserId,
    connectionRetryCount,
    maxConnectionRetries,
    // Debug method to check listener counts
    getListenerStats: () => socket.socketService?.getListenerStats?.(),
    // Methods to clear listeners for debugging
    clearEventListeners: (event) => socket.socketService?.clearEventListeners?.(event),
    clearAllListeners: () => socket.socketService?.clearAllListeners?.(),
    // Connection health check
    checkConnectionHealth: () => {
      const health = socket.checkConnectionHealth();
      return health;
    },
    // Force reconnection for debugging/testing
    debugReconnect: () => {
      return socket.forceReconnect();
    },
    // Add messageEdited listener support
    addMessageEditedListener: (callback) => socket.socketService?.addEventListener('messageEdited', callback)
  }), [
    socket,
    isInitialized,
    isAuthenticated,
    currentUserId,
    connectionRetryCount,
    maxConnectionRetries
  ]);
  


  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
